export interface Lesson {
  id: number
  title: {
    en: string
    ar: string
  }
  description: {
    en: string
    ar: string
  }
  duration: string
  durationAr: string
  isCompleted: boolean
  objectives?: {
    en: string[]
    ar: string[]
  }
  examples?: any[] // Type varies based on lesson type
  unlockCondition?: {
    lessonId?: number
    unitProgress?: number
  }
}
export interface Unit {
  id: number
  title: {
    en: string
    ar: string
  }
  description: {
    en: string
    ar: string
  }
  lessons: Lesson[]
  difficulty: 'beginner' | 'intermediate' | 'advanced'
  category: string
  estimatedTime: string
  estimatedTimeAr: string
  unlockCondition?: {
    unitId?: number
    progress?: number
  }
  progress: number
  isLocked: boolean
}
export interface Course {
  id: number
  title: {
    en: string
    ar: string
  }
  description: {
    en: string
    ar: string
  }
  progress: number
  totalLessons: number
  completedLessons: number
  estimatedTime: string
  estimatedTimeAr: string
  units: Unit[]
}
export const LEARNING_PATH_DATA: Course[] = [
  {
    id: 1,
    title: {
      en: 'Getting Started with English',
      ar: 'البدء في تعلم الإنجليزية',
    },
    description: {
      en: 'Master the fundamentals of English language with our comprehensive beginner course',
      ar: 'أتقن أساسيات اللغة الإنجليزية مع دورتنا الشاملة للمبتدئين',
    },
    progress: 25,
    totalLessons: 12,
    completedLessons: 3,
    estimatedTime: '6 weeks',
    estimatedTimeAr: '6 أسابيع',
    units: [
      {
        id: 1,
        title: {
          en: 'Getting Started',
          ar: 'البداية',
        },
        description: {
          en: 'Learn essential English sounds, greetings, and numbers',
          ar: 'تعلم الأصوات الإنجليزية الأساسية والتحيات والأرقام',
        },
        progress: 0,
        isLocked: false,
        difficulty: 'beginner',
        category: 'essentials',
        estimatedTime: '2 hours',
        estimatedTimeAr: 'ساعتين',
        lessons: [
          {
            id: 1,
            title: {
              en: 'Introduction to English Sounds',
              ar: 'مقدمة في أصوات اللغة الإنجليزية',
            },
            description: {
              en: 'Learn the fundamental sounds of English letters and words',
              ar: 'تعلم الأصوات الأساسية للحروف والكلمات الإنجليزية',
            },
            duration: '15 min',
            durationAr: '15 دقيقة',
            isCompleted: true,
          },
          {
            id: 2,
            title: {
              en: 'Basic Greetings',
              ar: 'التحيات الأساسية',
            },
            description: {
              en: 'Learn essential greetings and introductions',
              ar: 'تعلم التحيات والمقدمات الأساسية',
            },
            duration: '20 min',
            durationAr: '20 دقيقة',
            isCompleted: false,
            unlockCondition: {
              lessonId: 1,
            },
          },
          {
            id: 3,
            title: {
              en: 'Numbers and Counting',
              ar: 'الأرقام والعد',
            },
            description: {
              en: 'Master numbers and basic counting',
              ar: 'أتقن الأرقام والعد الأساسي',
            },
            duration: '25 min',
            durationAr: '25 دقيقة',
            isCompleted: false,
            unlockCondition: {
              lessonId: 2,
            },
          },
        ],
      },
      {
        id: 2,
        title: {
          en: 'Daily Conversations',
          ar: 'المحادثات اليومية',
        },
        description: {
          en: 'Practice common phrases and everyday conversations',
          ar: 'تدرب على العبارات الشائعة والمحادثات اليومية',
        },
        progress: 0,
        isLocked: true,
        difficulty: 'beginner',
        category: 'conversation',
        estimatedTime: '3 hours',
        estimatedTimeAr: '3 ساعات',
        unlockCondition: {
          unitId: 1,
          progress: 100,
        },
        lessons: [
          {
            id: 1,
            title: {
              en: 'Meeting New People',
              ar: 'مقابلة أشخاص جدد',
            },
            description: {
              en: 'Learn how to introduce yourself and others',
              ar: 'تعلم كيفية تقديم نفسك والآخرين',
            },
            duration: '20 min',
            durationAr: '20 دقيقة',
            isCompleted: false,
          },
          {
            id: 2,
            title: {
              en: 'At the Restaurant',
              ar: 'في المطعم',
            },
            description: {
              en: 'Practice ordering food and making reservations',
              ar: 'تدرب على طلب الطعام وحجز الطاولات',
            },
            duration: '25 min',
            durationAr: '25 دقيقة',
            isCompleted: false,
            unlockCondition: {
              lessonId: 1,
            },
          },
        ],
      },
      {
        id: 3,
        title: {
          en: 'Practical English',
          ar: 'الإنجليزية العملية',
        },
        description: {
          en: 'Learn practical English for real-world situations',
          ar: 'تعلم الإنجليزية العملية للمواقف الحياتية',
        },
        progress: 0,
        isLocked: true,
        difficulty: 'beginner',
        category: 'practical',
        estimatedTime: '4 hours',
        estimatedTimeAr: '4 ساعات',
        unlockCondition: {
          unitId: 2,
          progress: 100,
        },
        lessons: [
          {
            id: 1,
            title: {
              en: 'At the Restaurant',
              ar: 'في المطعم',
            },
            description: {
              en: 'Learn how to order food and interact at restaurants',
              ar: 'تعلم كيفية طلب الطعام والتفاعل في المطاعم',
            },
            duration: '30 min',
            durationAr: '30 دقيقة',
            isCompleted: false,
            objectives: {
              en: [
                'Order food and drinks',
                'Ask about menu items',
                'Make special requests',
                'Handle payment',
              ],
              ar: [
                'طلب الطعام والمشروبات',
                'السؤال عن عناصر القائمة',
                'تقديم طلبات خاصة',
                'التعامل مع الدفع',
              ],
            },
          },
          {
            id: 2,
            title: {
              en: 'Shopping',
              ar: 'التسوق',
            },
            description: {
              en: 'Learn essential shopping vocabulary and expressions',
              ar: 'تعلم مفردات وتعبيرات التسوق الأساسية',
            },
            duration: '25 min',
            durationAr: '25 دقيقة',
            isCompleted: false,
            unlockCondition: {
              lessonId: 1,
            },
            objectives: {
              en: [
                'Ask about prices',
                'Compare items',
                'Request different sizes',
                'Make purchases',
              ],
              ar: [
                'السؤال عن الأسعار',
                'مقارنة العناصر',
                'طلب مقاسات مختلفة',
                'إجراء المشتريات',
              ],
            },
          },
          {
            id: 3,
            title: {
              en: 'Public Transportation',
              ar: 'المواصلات العامة',
            },
            description: {
              en: 'Navigate public transportation with confidence',
              ar: 'تنقل في وسائل النقل العام بثقة',
            },
            duration: '35 min',
            durationAr: '35 دقيقة',
            isCompleted: false,
            unlockCondition: {
              lessonId: 2,
            },
            objectives: {
              en: [
                'Buy tickets',
                'Ask for directions',
                'Understand announcements',
                'Handle delays and changes',
              ],
              ar: [
                'شراء التذاكر',
                'طلب الاتجاهات',
                'فهم الإعلانات',
                'التعامل مع التأخير والتغييرات',
              ],
            },
          },
          {
            id: 4,
            title: {
              en: 'Making Appointments',
              ar: 'تحديد المواعيد',
            },
            description: {
              en: 'Schedule and manage appointments effectively',
              ar: 'جدولة وإدارة المواعيد بفعالية',
            },
            duration: '30 min',
            durationAr: '30 دقيقة',
            isCompleted: false,
            unlockCondition: {
              lessonId: 3,
            },
            objectives: {
              en: [
                'Schedule appointments',
                'Change or cancel meetings',
                'Discuss availability',
                'Confirm details',
              ],
              ar: [
                'تحديد المواعيد',
                'تغيير أو إلغاء الاجتماعات',
                'مناقشة التوفر',
                'تأكيد التفاصيل',
              ],
            },
          },
        ],
      },
    ],
  },
]
export const calculateUnitProgress = (unit: Unit): number => {
  if (unit.lessons.length === 0) return 0
  const completedLessons = unit.lessons.filter((lesson) => lesson.isCompleted).length
  return Math.round((completedLessons / unit.lessons.length) * 100)
}
export const calculateCourseProgress = (course: Course): number => {
  const totalLessons = course.units.reduce(
    (total, unit) => total + unit.lessons.length,
    0
  )
  const completedLessons = course.units.reduce(
    (total, unit) =>
      total + unit.lessons.filter((lesson) => lesson.isCompleted).length,
    0
  )
  return Math.round((completedLessons / totalLessons) * 100)
}
export const isLessonUnlocked = (
  lesson: Lesson,
  unit: Unit,
  previousLessons: Lesson[]
): boolean => {
  if (!lesson.unlockCondition) return true
  if (lesson.unlockCondition.lessonId) {
    const requiredLesson = previousLessons.find(
      (l) => l.id === lesson.unlockCondition?.lessonId
    )
    return requiredLesson?.isCompleted ?? false
  }
  if (lesson.unlockCondition.unitProgress) {
    return unit.progress >= lesson.unlockCondition.unitProgress
  }
  return true
}
export const isUnitUnlocked = (
  unit: Unit,
  previousUnits: Unit[]
): boolean => {
  if (!unit.unlockCondition) return true
  if (unit.unlockCondition.unitId) {
    const requiredUnit = previousUnits.find(
      (u) => u.id === unit.unlockCondition?.unitId
    )
    return (requiredUnit?.progress ?? 0) >= (unit.unlockCondition.progress ?? 0)
  }
  return true
}